import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { Kanban, Plus, UserPlus } from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Card } from '@/components/card';
import { EmptyState } from '@/components/empty-state';
import { PageHeader } from '@/components/page-header';
import { SkeletonList } from '@/components/skeleton';
import { useToast } from '@/components/toast';
import { dollars, formatStatus } from '@/lib/format';
import { LEAD_STAGES, useLeads, useUpdateLead, type LeadStage } from '@/lib/leads';
import { SOURCE_ICON, SOURCE_LABEL } from './index';
import type { LeadData } from '@/types/api';

const STAGE_TONE: Record<LeadStage, { dot: string; bg: string }> = {
  new: { dot: 'bg-slate-400', bg: 'bg-slate-50 dark:bg-slate-900/30' },
  contacted: { dot: 'bg-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/20' },
  qualified: { dot: 'bg-violet-500', bg: 'bg-violet-50 dark:bg-violet-900/20' },
  proposal: { dot: 'bg-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/20' },
  won: { dot: 'bg-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
  lost: { dot: 'bg-rose-500', bg: 'bg-rose-50 dark:bg-rose-900/20' },
};

export function LeadsPipeline() {
  const { data: paginated, isLoading } = useLeads({ page: 1 });
  const update = useUpdateLead();
  const toast = useToast();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const [activeId, setActiveId] = useState<number | null>(null);

  // Group leads by stage. Memoized so drag-overlay doesn't thrash.
  const byStage = useMemo<Record<string, LeadData[]>>(() => {
    const out: Record<string, LeadData[]> = {};
    LEAD_STAGES.forEach((s) => {
      out[s] = [];
    });
    (paginated?.data ?? []).forEach((l) => {
      (out[l.stage] ??= []).push(l);
    });
    return out;
  }, [paginated]);

  const activeLead =
    activeId != null ? (paginated?.data ?? []).find((l) => l.id === activeId) ?? null : null;

  function onDragStart(e: DragStartEvent) {
    setActiveId(Number(e.active.id));
  }

  async function onDragEnd(e: DragEndEvent) {
    setActiveId(null);
    const overStage = e.over?.id?.toString();
    const leadId = Number(e.active.id);
    if (!overStage || !LEAD_STAGES.includes(overStage as LeadStage)) return;

    const lead = (paginated?.data ?? []).find((l) => l.id === leadId);
    if (!lead || lead.stage === overStage) return;

    try {
      await update.mutateAsync({ id: leadId, patch: { stage: overStage as LeadStage } });
    } catch {
      toast.error('Could not move lead');
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Kanban}
        title="Pipeline"
        subtitle="Drag a lead between stages. Stage timestamps are auto-recorded."
        action={
          <Link to="/leads/new">
            <Button>
              <Plus className="size-4" /> New lead
            </Button>
          </Link>
        }
      />

      {isLoading ? (
        <Card className="p-4">
          <SkeletonList rows={3} />
        </Card>
      ) : (paginated?.data ?? []).length === 0 ? (
        <EmptyState
          icon={Kanban}
          title="Nothing in the pipeline yet"
          description="Add a lead to see it land in 'New'."
          action={
            <Link to="/leads/new">
              <Button>
                <Plus className="size-4" /> New lead
              </Button>
            </Link>
          }
        />
      ) : (
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {LEAD_STAGES.map((stage) => (
              <StageColumn key={stage} stage={stage} leads={byStage[stage] ?? []} />
            ))}
          </div>
          <DragOverlay>
            {activeLead ? <LeadCard lead={activeLead} dragging /> : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}

function StageColumn({ stage, leads }: { stage: LeadStage; leads: LeadData[] }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  const totalCents = leads.reduce((sum, l) => sum + (l.value_estimate_cents ?? 0), 0);
  const tone = STAGE_TONE[stage];

  return (
    <div className="flex min-h-[420px] flex-col">
      <div className="mb-2 flex items-center justify-between gap-2 px-1">
        <div className="flex items-center gap-2">
          <span className={`size-1.5 rounded-full ${tone.dot}`} aria-hidden />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {formatStatus(stage)}
          </span>
          <Badge tone="neutral">{leads.length}</Badge>
        </div>
        {totalCents > 0 ? (
          <span className="text-[11px] font-medium tabular-nums text-muted-foreground">
            {dollars(totalCents)}
          </span>
        ) : null}
      </div>
      <div
        ref={setNodeRef}
        className={`flex-1 rounded-lg border border-dashed transition-colors ${
          isOver
            ? 'border-primary/60 bg-primary/5'
            : 'border-border/60 ' + tone.bg
        } space-y-2 p-2`}
      >
        {leads.length === 0 ? (
          <div className="flex h-full items-center justify-center py-8 text-xs text-muted-foreground">
            Drop leads here
          </div>
        ) : (
          leads.map((lead) => <DraggableLeadCard key={lead.id} lead={lead} />)
        )}
      </div>
    </div>
  );
}

function DraggableLeadCard({ lead }: { lead: LeadData }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: lead.id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={isDragging ? 'opacity-30' : ''}
    >
      <LeadCard lead={lead} />
    </div>
  );
}

function LeadCard({ lead, dragging = false }: { lead: LeadData; dragging?: boolean }) {
  const navigate = useNavigate();
  const Icon = SOURCE_ICON[lead.source] ?? UserPlus;
  return (
    <div
      className={`group/lc cursor-grab rounded-md border border-border/70 bg-card px-3 py-2 text-sm shadow-xs transition-shadow ${
        dragging ? 'rotate-1 shadow-elev' : 'hover:border-primary/40'
      }`}
      onClick={() => {
        if (!dragging) navigate(`/leads/${lead.id}`);
      }}
    >
      <div className="flex items-baseline gap-2">
        <Icon
          className="size-3.5 shrink-0 text-muted-foreground"
          aria-label={SOURCE_LABEL[lead.source] ?? lead.source}
        />
        <span className="truncate font-medium text-foreground">{lead.name}</span>
      </div>
      <div className="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="truncate">{lead.email ?? lead.phone ?? '—'}</span>
        {lead.value_estimate_cents != null ? (
          <span className="shrink-0 tabular-nums">{dollars(lead.value_estimate_cents)}</span>
        ) : null}
      </div>
    </div>
  );
}
